import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, List, ListItem, ListItemText, Stack, Typography,
} from '@mui/material';
import { MarkEmailUnread as FailedIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Re-checked periodically: a send that fails does so after the page has loaded. */
const POLL_MS = 60000;

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Messages to patients whose email did not arrive, and the two ways to put it right.
 *
 * The in-app copy always lands, so nothing is lost — but a patient who does not sign in
 * never learns their doctor was assigned or their summary is ready. Until this existed
 * that failure was a line in a log file, and the front desk had no way to know.
 *
 * Staff and clinic admins only: chasing an undelivered message is desk work, and a
 * clinician mid-consultation does not need it.
 */
export default function UndeliveredMessages({ enabled }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/admin/notifications/undelivered`);
      setRows(data?.notifications || []);
    } catch {
      setRows([]);          // never let this break the dashboard around it
    }
  }, [enabled]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const resend = async (row, channel) => {
    setBusyId(row.id);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/v1/admin/notifications/${row.id}/resend`, { channel });
      if (channel === 'notify') {
        toast.success('Put back in the patient’s portal');
      } else if (data?.email_sent) {
        toast.success(`Email sent to ${row.patient_email}`);
      } else {
        // Deliberately not a success: the retry ran and the mail server refused again.
        toast.error(data?.error ? `Still failing — ${data.error}` : 'The email failed again');
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend that');
    } finally {
      setBusyId(null);
    }
  };

  if (!enabled || rows.length === 0) return null;

  return (
    <>
      <Alert
        severity="warning"
        icon={<FailedIcon />}
        sx={{ mb: 3 }}
        action={<Button color="inherit" size="small" onClick={() => setOpen(true)}>Review</Button>}
      >
        {rows.length === 1
          ? 'A message to a patient could not be emailed.'
          : `${rows.length} messages to patients could not be emailed.`}{' '}
        They can still see it in their portal, but will not know unless they sign in.
      </Alert>

      <Dialog open={open} onClose={() => !busyId && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Undelivered messages</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each of these is waiting in the patient’s portal — only the email failed.
            Retry it, put a fresh copy in their portal, or phone them.
          </Typography>
          <List disablePadding>
            {rows.map((r, i) => (
              <React.Fragment key={r.id}>
                {i > 0 && <Divider component="li" />}
                <ListItem
                  disableGutters
                  sx={{ display: 'block', py: 2 }}
                  secondaryAction={null}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Typography fontWeight={700}>{r.title}</Typography>
                    <Chip size="small" variant="outlined" label={r.patient_email || 'no address'} />
                    {r.attempts > 1 && (
                      <Chip size="small" color="warning" variant="outlined"
                        label={`${r.attempts} attempts`} />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {when(r.created_at)}
                    </Typography>
                  </Stack>
                  <ListItemText
                    sx={{ mt: 0.5 }}
                    primary={r.message}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    secondary={r.error ? `Mail server said: ${r.error}` : null}
                    secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      size="small" variant="contained" disabled={busyId === r.id || !r.patient_email}
                      startIcon={busyId === r.id ? <CircularProgress size={14} color="inherit" /> : null}
                      onClick={() => resend(r, 'email')}
                    >
                      Retry email
                    </Button>
                    <Button size="small" variant="outlined" disabled={busyId === r.id}
                      onClick={() => resend(r, 'notify')}>
                      Send to portal only
                    </Button>
                    <Button size="small" disabled={busyId === r.id}
                      onClick={() => resend(r, 'both')}>
                      Both
                    </Button>
                  </Stack>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setOpen(false)} disabled={Boolean(busyId)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
